/**
 * Axios Api Base File with Cognito Token
 *
 * Developed By : Travancore Analytics (2025)
 * Author: Akhil Soman
 * Created on: 11/03/2025
 * Project: Vitromed
 * Revision History:
 * SNo.   | Date        | Author        | Task                | Comments |
 * 1      11/03/2025    Akhil Soman       Creating new File
 */

import axios from "axios";
import { fetchAuthSession } from "aws-amplify/auth";
import Constant from "../global/Constant.ts";
import { handleSignOut } from "../utils/authUtils.ts";
import store from "../redux/store.ts"; // Add this import

const api = axios.create({
  baseURL: Constant.VBaseUrl,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add a request interceptor to attach the Cognito ID token 
api.interceptors.request.use(
  async (config) => {
    try {
      // Fetch the current authentication session
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;  // Get the ID token here

      // Attach the ID token to the request headers
      if (idToken) {
        config.headers.Authorization = `Bearer ${idToken}`;
      }

      return config;
    } catch (error) {
      console.error("Error fetching ID token:", error);
      throw error;
    }
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add a response interceptor to handle token expiration or other errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      console.error("Unauthorized access. Redirecting to login...");
      try {
        // Only pass store.dispatch since we'll handle navigation via window.location
        await handleSignOut(store.dispatch);
        // Force full page reload to clear state
        window.location.href = "/";
      } catch (err) {
        console.error("Error during sign out:", err);
      }
    }
    return Promise.reject(error);
  }
);

export default api;
